import React, { createContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import {
  createStudentHistory,
  getAllStudentHistories,
  getStudentHistoryById,
  getStudentHistoryByStudentAndYear,
  getStudentHistoryByStudentId,
  updateStudentHistory,
  deleteStudentHistory
} from '../services/studentHistoryService';
import Notification from '../components/Common/Notification';

export const StudentHistoryContext = createContext();

export const StudentHistoryProvider = ({ children }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [studentHistories, setStudentHistories] = useState([]);
  const [selectedStudentHistory, setSelectedStudentHistory] = useState(null);
  const [notification, setNotification] = useState({ message: '', isError: false });

  const fetchAuthHeaders = () => {
    const storedUser = localStorage.getItem('user');
    const token = user?.token || (storedUser ? JSON.parse(storedUser).token : '');

    if (!token) {
      console.error('User or token not available');
      return {};
    }

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const showNotification = (message, isError) => {
    setNotification({ message, isError });
  };

  const closeNotification = () => {
    setNotification({ message: '', isError: false });
  };

  const fetchAllStudentHistories = async () => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getAllStudentHistories(authHeaders);
      setStudentHistories(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error fetching student histories.';
      showNotification(errorMessage, true);
    }
  };

  const fetchStudentHistoryById = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getStudentHistoryById(id, authHeaders);
      setSelectedStudentHistory(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || `Error fetching student history with ID ${id}.`;
      showNotification(errorMessage, true);
    }
  };

  const fetchStudentHistoryByStudentAndYear = async (studentId, academicYearId) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getStudentHistoryByStudentAndYear(studentId, academicYearId, authHeaders);
      setSelectedStudentHistory(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || `Error fetching student history for student ID ${studentId} and year ${academicYearId}.`;
      showNotification(errorMessage, true);
    }
  };

  const fetchStudentHistoryByStudentId = async (studentId) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getStudentHistoryByStudentId(studentId, authHeaders);
      setStudentHistories(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || `Error fetching student history for student ID ${studentId}.`;
      showNotification(errorMessage, true);
    }
  };

  const createNewStudentHistory = async (studentHistoryData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const newStudentHistory = await createStudentHistory(studentHistoryData, authHeaders);
      setStudentHistories(prevHistories => [...prevHistories, newStudentHistory]);
      showNotification('Student history created successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error creating student history.';
      showNotification(errorMessage, true);
    }
  };

  const updateExistingStudentHistory = async (id, studentHistoryData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const updatedStudentHistory = await updateStudentHistory(id, studentHistoryData, authHeaders);
      setStudentHistories(prevHistories =>
        prevHistories.map(history => (history.id === id ? updatedStudentHistory : history))
      );
      showNotification('Student history updated successfully!', false);
      return updatedStudentHistory;
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error updating student history.';
      showNotification(errorMessage, true);
      throw error;
    }
  };

  const removeStudentHistory = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      await deleteStudentHistory(id, authHeaders);
      setStudentHistories(prevHistories => prevHistories.filter(history => history.id !== id));
      showNotification('Student history removed successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error removing student history.';
      showNotification(errorMessage, true);
    }
  };

  // useEffect(() => {
  //   fetchAllStudentHistories();
  // }, []);


  useEffect(() => {
    if (!isAuthenticated && !['/', '/login'].includes(window.location.pathname)) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);


  return (
    <StudentHistoryContext.Provider
      value={{
        studentHistories,
        selectedStudentHistory,
        fetchAllStudentHistories,
        fetchStudentHistoryById,
        fetchStudentHistoryByStudentAndYear,
        fetchStudentHistoryByStudentId,
        createNewStudentHistory,
        updateExistingStudentHistory,
        removeStudentHistory,
      }}
    >
      {children}
      {notification.message && (
        <Notification
          message={notification.message}
          isError={notification.isError}
          onClose={closeNotification}
        />
      )}
    </StudentHistoryContext.Provider>
  );
};
